import { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { useGetUserImage } from "hooks/query/useAccount";
import { UserImageServiceParams } from "interfaces";
import avatar from "assets/svg/avatar.svg";

interface UserImageProps {
  userId: string;
  params?: UserImageServiceParams;
  width?: number;
  height?: number;
  rounded?: "full" | "lg" | "xl" | "2xl" | "3xl" | "none";
  fallback?: string;
  initials?: string;
  className?: string;
}

export default function UserImage({
  userId,
  params,
  width,
  height,
  rounded = "full",
  fallback = avatar,
  initials,
  className = "",
}: UserImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const { data: imageData, isLoading } = useGetUserImage({
    id: userId,
    params,
  });

  useEffect(() => {
    if (!imageData) {
      setImageUrl(null);
      return;
    }
    if (typeof imageData === "string") {
      setImageUrl(imageData);
    } else if (imageData.size > 0) {
      const url = URL.createObjectURL(imageData);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImageUrl(null);
    }
  }, [imageData]);

  const roundedClass =
    rounded === "full"
      ? "rounded-full"
      : rounded === "none"
        ? ""
        : `rounded-${rounded}`;

  // Only apply inline size when explicit width/height are provided
  const sizeStyle: React.CSSProperties = {};
  if (width !== undefined) sizeStyle.width = width;
  if (height !== undefined) sizeStyle.height = height;

  if (isLoading) {
    return (
      <div
        style={sizeStyle}
        className={`${roundedClass} flex items-center justify-center bg-orange30 ${className}`}
      >
        <ClipLoader color="gray" size={14} />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt="avatar"
        style={sizeStyle}
        className={`${roundedClass} object-cover ${className} object-center`}
      />
    );
  }

  if (initials) {
    return (
      <div
        style={{ ...sizeStyle, backgroundColor: "#F5693F" }}
        className={`${roundedClass} flex items-center justify-center ${className}`}
      >
        <span className="text-white font-semibold text-xl">{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={fallback}
      alt="avatar"
      style={sizeStyle}
      className={`${roundedClass} object-cover ${className}`}
    />
  );
}
